import React, { Fragment, useEffect, useMemo } from 'react'
import { isEmpty } from 'lodash'
import { useForm, Controller, useFieldArray, FormProvider } from 'react-hook-form'
import {
  checkFormData,
  checkTimeSlots,
  createTimeSlot,
  formatSlotTime,
  removeEmptySlots,
} from 'utils/helper'
import { FORM, TIME_SLOT, slots, timeDurationAvailabilityOptions } from 'config'
import Slots from 'components/Calendar/EventForm/Slots'
import CheckBoxField from 'components/Common/CheckBoxField'
import Button from 'components/Common/Button'
import useFormContainer from 'hooks/useFormContainer'
import Grid from 'components/Common/Grid'
import Loader from 'components/Loaders'
import {
  CreateEventSlots,
  CreateEventTitle,
  CreateEventHeading,
  CreateEventWrapper,
  CreateEventSlotsSpan,
  CreateEventMeetingTitle,
  CreateEventCheckBoxWrapper,
  CreateEventSlotsWrapper,
  CreateEventButtonWrapper,
  CreateEventTimezoneSpan,
  CreateEventMeetingDescription,
} from 'components/Calendar/EventForm/style'
import { getTimeOptions } from 'components/Calendar/EventForm/helper'
import MeetingSettingsEditor, {
  MeetingSettingsEditorStyle,
} from 'components/Common/MeetingSettingsEditor'
import { useCurrentTime } from 'hooks/useCurrentTime'
import {
  AvailabilityHoursObj,
  SetUserAvailabilityInput,
  UpdateMyProfileInput,
  UpdateUserAvailabilityInput,
  UserProfile,
} from '../../../__generated__/api-types-and-hooks'
import moment from 'moment-timezone'
import { ReduxAction } from '../../../store/types'
import { IAdvisorForm } from 'types'
import Tile from 'components/Common/Tile'
import { getTimeZoneDisplayRepresentation } from 'utils/dates'

const DEFAULT_SLOT_INTERVAL = 15

export interface ITimeSlotOption {
  label: string
  value: string
}

interface ICreateEventProps {
  onClose?: (input: boolean) => void
  handleOnSubmit?: (input: { defaultVideoLinkDescription?: string }) => void
  userAvailabilityHoursList: AvailabilityHoursObj
  isAvailabilityHoursLoading: boolean
  setUserAvailabilityAction: ReduxAction<SetUserAvailabilityInput>
  getUserAvailabilityHoursAction: (input: { setForms: FORM[]; setValue }) => void
  updateUserAvailabilityHoursAction: ReduxAction<UpdateUserAvailabilityInput>
  updateUserProfileAction: ReduxAction<Partial<UpdateMyProfileInput>>
  getUserProfileAction: (input: { setValue }) => void
  userProfile: UserProfile
}

function CreateEvent({
  onClose,
  handleOnSubmit,
  userAvailabilityHoursList,
  isAvailabilityHoursLoading,
  setUserAvailabilityAction,
  getUserAvailabilityHoursAction,
  updateUserAvailabilityHoursAction,
  updateUserProfileAction,
  getUserProfileAction,
  userProfile,
}: ICreateEventProps) {
  const methods = useForm<IAdvisorForm>({})
  useEffect(() => {
    getUserAvailabilityHoursAction({ setForms: [FORM.USER_AVAILABILITY], setValue })
    getUserProfileAction({ setValue })
    // eslint-disable-next-line
  }, [getUserAvailabilityHoursAction, getUserProfileAction])

  const { control, register, handleSubmit, getValues, setValue } = methods
  const { fields } = useFieldArray({
    control,
    name: 'timeSlots',
  })

  let timeSlots
  if (getValues()?.timeSlots) {
    timeSlots = [...getValues()?.timeSlots] || []
  }

  const handleCheckBox = (position, checked) => {
    timeSlots[position].checked = checked
    if (!checked) {
      timeSlots[position].slots = []
    } else {
      timeSlots[position].slots = [
        { startTime: slots.defaultStartTime, endTime: slots.defaultEndTime },
      ]
    }
    handleSetValue(false, timeSlots, position)
  }

  const handleSetValue = (isNewSlot, timeSlot, parentIndex) => {
    if (isNewSlot) {
      let timeSlotsLength = timeSlots[parentIndex].slots.length
      timeSlots[parentIndex].slots[timeSlotsLength] = { startTime: '', endTime: '' }
      setValue('timeSlots', [...timeSlot])
    } else {
      setValue('timeSlots', [...timeSlot])
    }
  }

  useFormContainer(FORM.USER_AVAILABILITY, methods)

  // const calendarForm = hookForms.getForm(FORM.USER_AVAILABILITY)
  const calendarFormValue = getValues()
  const timezone = userProfile.timezone
  const currentTime = useCurrentTime(timezone ?? moment.tz.guess())
  const formattedTimezone = getTimeZoneDisplayRepresentation(timezone ?? moment.tz.guess())

  const onSubmit = (data) => {
    const updatedTimeSlots = removeEmptySlots(data.timeSlots)

    let availabilityHours = {
      duration: calendarFormValue?.duration,
      timeSlots: updatedTimeSlots,
    }
    !isEmpty(userAvailabilityHoursList?.timeSlots)
      ? updateUserAvailabilityHoursAction({ availabilityHours })
      : setUserAvailabilityAction({ availabilityHours })

    if (handleOnSubmit) {
      handleOnSubmit({ defaultVideoLinkDescription: data.defaultVideoLinkDescription })
    } else if (data.defaultVideoLinkDescription) {
      updateUserProfileAction({
        defaultVideoLinkDescription: data.defaultVideoLinkDescription,
      })
    }
  }

  const handleSlotChange = (parentIndex, childIndex, value, isStartTime) => {
    const timeSlots = getValues()?.timeSlots.slice()
    const currentSlot = timeSlots[parentIndex]?.slots[childIndex]
    let { startTime, endTime } = currentSlot
    const formattedStartTime = formatSlotTime(startTime)
    const formattedEndTime = formatSlotTime(endTime)
    const formattedValue = formatSlotTime(value)

    //if starttime is greater || equal to endtime
    if (isStartTime && value >= endTime && endTime !== '') {
      timeSlots[parentIndex].slots[childIndex].endTime = ''
      timeSlots[parentIndex].slots[childIndex].startTime = value
    } else if (!isStartTime && value <= startTime) {
      return
    }

    if (isStartTime && timeSlotsOptions[timeSlotsOptions.length - 1].value === value) return

    const isOverlapExist = timeSlots[parentIndex].slots.some((slot, index) => {
      if (index !== childIndex) {
        const slotStartTime = slot.startTime || ''
        const slotEndTime = slot.endTime || ''
        if (value > slotStartTime && value < slotEndTime) {
          return true
        } else if (isStartTime && value === slotStartTime) {
          return true
        }
      }
      return false
    })

    if (isOverlapExist) {
      return
    }

    //Creating slots
    if (
      (!isStartTime && startTime && formattedValue) ||
      (isStartTime && endTime && formattedValue)
    ) {
      const timeSlotsCreation = createTimeSlot(
        isStartTime ? formattedValue : formattedStartTime,
        !isStartTime ? formattedValue : formattedEndTime,
        DEFAULT_SLOT_INTERVAL
      )
      const isExist = checkTimeSlots(timeSlots[parentIndex].slots, timeSlotsCreation, childIndex)
      if (isStartTime && isExist) {
        timeSlots[parentIndex].slots[childIndex].endTime = ''
        timeSlots[parentIndex].slots[childIndex].startTime = value
      } else if (isExist) {
        return
      }
    }
    if (isStartTime) timeSlots[parentIndex].slots[childIndex].startTime = value
    else timeSlots[parentIndex].slots[childIndex].endTime = value
    //sort
    timeSlots[parentIndex].slots.sort((a, b) => {
      const startTimeA = a.startTime || ''
      const startTimeB = b.startTime || ''

      if (startTimeA < startTimeB) return -1
      if (startTimeA > startTimeB) return 1
      return 0
    })
    setValue('timeSlots', [...timeSlots])
    return
  }
  const timeSlotOptions: ITimeSlotOption[] = createTimeSlot(
    TIME_SLOT.START_TIME,
    TIME_SLOT.END_TIME,
    DEFAULT_SLOT_INTERVAL
  )

  const timeSlotsOptions: ITimeSlotOption[] = useMemo(
    () => getTimeOptions(timeSlotOptions),
    [timeSlotOptions]
  )

  const handleMultiTimeDurationAvailability = (duration: string) => {
    const durations = calendarFormValue.duration
    let index = durations.indexOf(duration)
    if (index !== -1) {
      const updatedDurations = [...durations.slice(0, index), ...durations.slice(index + 1)]
      setValue('duration', updatedDurations)
    } else {
      setValue('duration', [...durations, duration])
    }
  }

  return isAvailabilityHoursLoading ? (
    <Loader loader={'AvailabilityHoursLoader'} />
  ) : (
    <Fragment>
      <CreateEventWrapper>
        <CreateEventHeading>
          Define the times your guests can book a meeting with you.
        </CreateEventHeading>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CreateEventMeetingTitle>Meeting duration:</CreateEventMeetingTitle>
            <CreateEventMeetingDescription>
              Select one or more durations for your client to choose from.
            </CreateEventMeetingDescription>
            <div className="flex flex-row gap-5">
              {timeDurationAvailabilityOptions.map((option) => (
                <Tile
                  onChange={handleMultiTimeDurationAvailability}
                  value={calendarFormValue.duration}
                  label={option.label}
                  optionValue={option.value}
                  className={'w-[149px] !h-[73px]'}
                  isCheckBox={true}
                />
              ))}
            </div>

            <CreateEventTitle>Time zone:</CreateEventTitle>
            <Grid>
              <CreateEventTimezoneSpan>
                <span className="inline-block font-medium	text-base	text-black-light mr-2 mt-1">
                  {formattedTimezone}
                </span>
                <span className="inline-block font-medium	text-base	text-black-light">
                  ({currentTime})
                </span>
              </CreateEventTimezoneSpan>
            </Grid>

            <CreateEventTitle>Set your weekly hours:</CreateEventTitle>

            <CreateEventSlots>
              <Grid className="gap-3">
                {/* @ts-ignore */}
                <CreateEventSlotsSpan>
                  {fields?.map((field, index) => {
                    return (
                      <Grid
                        className="!items-baseline border-b border-primary-disabled my-6 xs:relative sm:static"
                        key={index}
                      >
                        <CreateEventCheckBoxWrapper>
                          <CheckBoxField
                            {...field}
                            value={field.checked}
                            id={`custom-checkbox-${index}`}
                            className={'inline-flex pt-[2px]'}
                            onChange={(e) => handleCheckBox(index, e.target.checked)}
                          />
                        </CreateEventCheckBoxWrapper>
                        <CreateEventSlotsWrapper>
                          <Slots
                            field={field}
                            parentIndex={index}
                            timeSlots={timeSlots}
                            timeSlotOptions={timeSlotOptions}
                            handleSetValue={handleSetValue}
                            handleSlotChange={handleSlotChange}
                            {...{ control, register }}
                          />
                        </CreateEventSlotsWrapper>
                      </Grid>
                    )
                  })}
                </CreateEventSlotsSpan>
              </Grid>
            </CreateEventSlots>
            {/* <Grid className="mt-4">
              <span className="col-span-12 flex flex-row items-center sm:px-4 font-normal text-sm text-primary-text">
                <InfoIcon className="fill-blue-light w-4 h-4 mr-2" />
                You can change your time zone in account settings.
              </span>
            </Grid> */}
            <Controller
              name="defaultVideoLinkDescription"
              render={({ field }) => (
                <>
                  <CreateEventTitle>Set your default meeting link</CreateEventTitle>
                  <Grid>
                    <CreateEventTimezoneSpan>
                      <MeetingSettingsEditor
                        {...field}
                        value={userProfile.defaultVideoLinkDescription ?? ''}
                        style={MeetingSettingsEditorStyle.SECONDARY}
                      />
                    </CreateEventTimezoneSpan>
                  </Grid>
                </>
              )}
            />

            <div className="flex justify-center sm:justify-end">
              <CreateEventButtonWrapper>
                {onClose && (
                  <Button
                    label="Cancel"
                    type="button"
                    variant="secondary"
                    onClick={() => onClose(false)}
                    fontSize="xxl"
                  />
                )}
                <Button
                  label="Save"
                  type="submit"
                  variant="primary"
                  disabled={!checkFormData(getValues())}
                  fontSize="xxl"
                />
              </CreateEventButtonWrapper>
            </div>
          </form>
        </FormProvider>
      </CreateEventWrapper>
    </Fragment>
  )
}

export default CreateEvent
